import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';

import { Card, Flex, Box, Text, FlexColumn } from 'core/components';
import { formConsumer, InputMultiple } from 'core/form';
import {
  TCP_FLAG_OPTIONS,
  OPERATOR_OPTIONS,
  mitigationMethodFieldToMitigationDetailsMap,
  MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP
} from '@kentik/ui-shared/mitigations/constants';
import { FlowSpecMatchHeader, matchFieldTypeHash } from 'app/views/settings/mitigations/methods/FlowSpecMatchField';
import Tag from 'core/components/Tag';

const FlowSpecMatchInfo = ({ matchField, valueOptions, mb, operators }) => {
  const validOptions = operators || OPERATOR_OPTIONS;
  const selectedOption = validOptions.find((option) => option.value === matchField.operator.getValue());
  const selectedValue = valueOptions
    ? (valueOptions.find((option) => option.value === matchField.value.getValue())?.label ??
      matchField.value.getValue())
    : matchField.value.getValue();

  return (
    <Box>
      <Flex align="flex-start" pr={1} mb={mb} justifyContent="flex-end">
        <Flex alignSelf="center" mr={1}>
          {selectedOption.label}
        </Flex>
        <Tag>{selectedValue}</Tag>
      </Flex>
    </Box>
  );
};

@inject('$dictionary')
@formConsumer
@observer
export default class FlowSpecMatchInfoField extends Component {
  state = { isOpen: false };

  componentDidMount() {
    const { form, matchFieldType } = this.props;

    this.setState({ isOpen: form.getValue(`flowspec.${matchFieldType}.enabled`) });
  }

  render() {
    const {
      form,
      minimal,
      matchFieldType,
      showInferField = false,
      alertModel,
      mitigationContext,
      operators,
      $dictionary: { filterFieldValueOptions }
    } = this.props;

    const { isOpen } = this.state;
    const enabledField = form.getField(`flowspec.${matchFieldType}.enabled`);
    const inferField = showInferField && form.getField(`flowspec.${matchFieldType}.infer`);
    const isInferred = showInferField && inferField.getValue();
    const matchesField = form.getField(`flowspec.${matchFieldType}.value.predicates`);
    const valueOptions =
      matchFieldType === 'tcpFlags'
        ? TCP_FLAG_OPTIONS
        : filterFieldValueOptions[matchFieldTypeHash[matchFieldType]] || null;
    const groupField = form.getField(`flowspec.${matchFieldType}.value.groups`);
    const totalConditionNumber = matchesField.getValue().length + groupField.getValue().length;

    if (!isOpen && minimal) {
      return null;
    }

    return (
      <Flex>
        <Box py="12px" width="100%">
          <Flex flexColumn>
            <Flex justifyContent="space-between" alignItems="flex-start" flex={1}>
              <Text mr={1}>{enabledField.label}:</Text>
              {!isOpen && !minimal && (
                <Text as="span" ml={1} color="#6e6e6e" small>
                  Not Considered
                </Text>
              )}
              {isOpen && !isInferred && (
                <FlexColumn alignItems="flex-end">
                  {!minimal && (
                    <Box>
                      <FlowSpecMatchHeader />
                    </Box>
                  )}
                  <Box alignItems="flex-end">
                    {minimal && totalConditionNumber > 1 ? (
                      <Text mr="12px">{totalConditionNumber}&nbsp;Conditions</Text>
                    ) : null}
                    {(!minimal || totalConditionNumber === 1) && (
                      <FlexColumn alignItems="flex-end">
                        <InputMultiple field={matchesField}>
                          {({ field }) => (
                            <Box>
                              {field.map((matchField) => (
                                <FlowSpecMatchInfo
                                  key={field._id + matchField.operator._id + matchField.value._id}
                                  matchField={matchField}
                                  label={matchFieldType}
                                  valueOptions={valueOptions}
                                  mb={1}
                                  operators={operators}
                                />
                              ))}
                            </Box>
                          )}
                        </InputMultiple>
                        <InputMultiple field={groupField}>
                          {({ field }) => (
                            <>
                              {field.map((group) => (
                                <Card
                                  key={field._id + group.predicates._id}
                                  my={matchesField.getValue().length > 0 ? '12px' : 0}
                                  p={1}
                                >
                                  <FlowSpecMatchHeader group />
                                  <Box>
                                    {group.predicates.map((predicateField, idx) => (
                                      <FlowSpecMatchInfo
                                        key={field._id + predicateField.operator._id + predicateField.value._id}
                                        matchField={predicateField}
                                        label={matchFieldType}
                                        valueOptions={valueOptions}
                                        mb={idx === group.predicates.size() - 1 ? 0 : 1}
                                        operators={operators}
                                      />
                                    ))}
                                  </Box>
                                </Card>
                              ))}
                            </>
                          )}
                        </InputMultiple>
                      </FlexColumn>
                    )}
                  </Box>
                </FlexColumn>
              )}
              {isInferred && !(alertModel || mitigationContext) && <Tag ml={1}>Inferred</Tag>}
              {isInferred && alertModel && (
                <Flex alignItems="center" justifyContent="center">
                  <Text>Inferred as&nbsp;</Text>
                  <Tag ml={1}>
                    {alertModel.dimensionToKeyPart?.[MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP[matchFieldType]] ??
                      'Unknown Value'}
                  </Tag>
                </Flex>
              )}
              {isInferred && mitigationContext && (
                <Flex alignItems="center" justifyContent="center">
                  <Text>Inferred as&nbsp;</Text>
                  <Tag ml={1}>
                    {mitigationContext.get('targetObj')?.[
                      mitigationMethodFieldToMitigationDetailsMap[matchFieldType]
                    ] ?? 'Unknown Value'}
                  </Tag>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    );
  }
}
